// File: frontend/src/views/public/NewsletterPreferences.js

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_CONFIG } from '../../config/api';
import apiService from '../../services/apiService';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import styles from './NewsletterPreferences.module.css';

const NewsletterPreferences = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');
  const token = searchParams.get('token');
  const correlationId = searchParams.get('correlation_id');
  
  const [status, setStatus] = useState('loading'); // loading, ready, saving, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [preferences, setPreferences] = useState({
    frequency: 'as_available',
    topics: 'everything'
  });
  const [originalPreferences, setOriginalPreferences] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  const hasLoaded = useRef(false);
  
  useEffect(() => {
    document.title = 'Newsletter Preferences - Allegory Insurance';
    
    if (hasLoaded.current) {
      return;
    }
    
    loadPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);
  
  useEffect(() => {
    if (originalPreferences) {
      const changed = 
        preferences.frequency !== originalPreferences.frequency ||
        preferences.topics !== originalPreferences.topics;
      setHasChanges(changed);
    }
  }, [preferences, originalPreferences]);
  
  const loadPreferences = async () => {
    hasLoaded.current = true;
    
    if (!userId || !token) {
      setStatus('error');
      setErrorMessage('Invalid preferences link. Please check the link from your email.');
      return;
    }
    
    try {
      console.log('📧 Loading newsletter preferences...', { 
        user_id: userId,
        has_token: !!token,
        correlation_id: correlationId
      });
      
      const response = await apiService.getNewsletterPreferences({
        user_id: userId,
        token: token,
        correlation_id: correlationId
      });
      
      if (response.success) {
        console.log('✅ Preferences loaded successfully');
        setUserData(response.data);
        setPreferences({
          frequency: response.data.preferences.frequency,
          topics: response.data.preferences.topics
        });
        setOriginalPreferences({
          frequency: response.data.preferences.frequency,
          topics: response.data.preferences.topics
        });
        setStatus('ready');
      } else {
        throw new Error(response.error || 'Failed to load preferences');
      }
      
    } catch (error) {
      console.error('❌ Load preferences error:', error);
      setStatus('error');
      
      if (error.message.includes('Invalid or expired')) {
        setErrorMessage('This preferences link is invalid or has expired. Please use the latest link from your email.');
      } else {
        setErrorMessage(error.message || 'Failed to load preferences. Please try again.');
      }
    }
  };
  
  const handleSavePreferences = async () => {
    setStatus('saving');
    setErrorMessage('');
    
    try {
      console.log('💾 Saving newsletter preferences...', { 
        user_id: userId,
        preferences: preferences
      });
      
      const response = await apiService.updateNewsletterPreferences({
        user_id: userId,
        enterprise_id: API_CONFIG.enterpriseId,
        token: token,
        preferences: preferences,
        language: navigator.language.toLowerCase() || 'en-ca',
        correlation_id: correlationId
      });
      
      if (response.success) {
        console.log('✅ Preferences saved successfully');
        setOriginalPreferences({ ...preferences });
        setStatus('success');
        
        // Reset to ready after 3 seconds
        setTimeout(() => {
          setStatus('ready');
        }, 3000);
      } else {
        throw new Error(response.error || 'Failed to save preferences');
      }
      
    } catch (error) {
      console.error('❌ Save preferences error:', error);
      setStatus('ready');
      setErrorMessage(error.message || 'Failed to save preferences. Please try again.');
    }
  };
  
  const handleFrequencyChange = (e) => {
    setPreferences(prev => ({ ...prev, frequency: e.target.value }));
  };
  
  const handleTopicsChange = (e) => {
    setPreferences(prev => ({ ...prev, topics: e.target.value }));
  };
  
  const handleBackToHome = () => {
    navigate('/');
  };
  
  const handleContactSupport = () => {
    navigate('/contact');
  };
  
  // Loading state
  if (status === 'loading') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          <LoadingSpinner />
          <p className={styles.loadingText}>Loading your preferences...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (status === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.brandHeader}>
            <span className={styles.logo}>💳</span>
            <h2 className={styles.brandName}>Allegory Insurance</h2>
          </div>
          
          <div className={styles.errorIcon}>
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#FEF2F2"/>
              <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          
          <h1 className={styles.title}>Unable to Load Preferences</h1>
          <div className={styles.errorBox}>
            <p className={styles.errorMessage}>{errorMessage}</p>
          </div>
          
          <p className={styles.helpText}>
            Please use the latest preferences link from your email. If you continue to have issues, contact our support team.
          </p>
          
          <div className={styles.buttonGroup}>
            <button 
              onClick={handleBackToHome}
              className={styles.primaryButton}
            >
              Back to Home
            </button>
            
            <button 
              onClick={handleContactSupport}
              className={styles.secondaryButton}
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Ready/Saving/Success state
  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.brandHeader}>
          <span className={styles.logo}>💳</span>
          <h2 className={styles.brandName}>Allegory Insurance</h2>
        </div>
        
        <div className={styles.preferencesIcon}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#EFF6FF"/>
            <path 
              d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" 
              stroke="#1E3A8A" 
              strokeWidth="2"
            />
            <path 
              d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" 
              stroke="#1E3A8A" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
        
        <h1 className={styles.title}>Newsletter Preferences</h1>
        <p className={styles.subtitle}>
          {userData?.first_name && `Hi ${userData.first_name}! `}
          Customize how you receive our newsletter.
        </p>
        
        {status === 'success' && (
          <div className={styles.successBanner}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#10B981"/>
              <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Preferences saved successfully!</span>
          </div>
        )}
        
        {errorMessage && (
          <div className={styles.errorBanner}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#EF4444"/>
              <path d="M12 8V12M12 16H12.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>{errorMessage}</span>
          </div>
        )}
        
        <div className={styles.preferencesForm}>
          {/* Frequency */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="#1E3A8A" strokeWidth="2"/>
                <path d="M16 2V6M8 2V6M3 10H21" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Email Frequency
            </label>
            <select 
              className={styles.select}
              value={preferences.frequency}
              onChange={handleFrequencyChange}
              disabled={status === 'saving'}
            >
              <option value="as_available">As Available (Recommended)</option>
              <option value="monthly">Monthly Digest</option>
              <option value="quarterly">Quarterly Digest</option>
            </select>
            <p className={styles.helpText}>
              {preferences.frequency === 'as_available' && 'Receive emails as we publish new content'}
              {preferences.frequency === 'monthly' && 'Receive a monthly summary of all content'}
              {preferences.frequency === 'quarterly' && 'Receive a quarterly summary every 3 months'}
            </p>
          </div>
          
          {/* Topics */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#1E3A8A" strokeWidth="2"/>
                <path d="M9 7H15M9 11H15M9 15H13" stroke="#1E3A8A" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Content Topics
            </label>
            <select 
              className={styles.select}
              value={preferences.topics}
              onChange={handleTopicsChange}
              disabled={status === 'saving'}
            >
              <option value="everything">Everything (Recommended)</option>
              <option value="tips">Insurance Tips Only</option>
              <option value="alerts">Rate Alerts Only</option>
            </select>
            <p className={styles.helpText}>
              {preferences.topics === 'everything' && 'Get all our content including tips, alerts, and updates'}
              {preferences.topics === 'tips' && 'Only receive insurance tips and educational content'}
              {preferences.topics === 'alerts' && 'Only receive rate alerts and price notifications'}
            </p>
          </div>
        </div>
        
        <div className={styles.buttonGroup}>
          <button 
            onClick={handleSavePreferences}
            className={styles.primaryButton}
            disabled={!hasChanges || status === 'saving'}
          >
            {status === 'saving' ? (
              <>
                <span className={styles.spinner}></span>
                Saving...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M17 21V13H7V21M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Save Preferences
              </>
            )}
          </button>
          
          <button 
            onClick={handleBackToHome}
            className={styles.secondaryButton}
            disabled={status === 'saving'}
          >
            Back to Home
          </button>
        </div>
        
        {userData && (
          <div className={styles.accountInfo}>
            <p className={styles.accountEmail}>
              Managing preferences for: <strong>{userData.email_address}</strong>
            </p>
            <p className={styles.accountNote}>
              Subscribed since {new Date(userData.subscribed_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterPreferences;